/**
 * 登录态本地 + 安卓原生双端存储
 * 解决 H5 换域名后 localStorage 清空导致需重新登录的问题：
 * - 进入页面：从原生恢复缺失的 userInfo/token，并将已有登录态回写原生
 * - 登录成功：同时写入 localStorage 与原生 putString
 * - 读取：优先 localStorage，无则从原生 getString 并回填本地
 * - 退出登录：清空 localStorage 与原生存储
 */
import { putString, getString, setAppUserId } from '@/assets/js/base'

/** 原生端 userInfo 存储键 */
export const NATIVE_KEY_USER_INFO = 'androidUserInfo'
/** 原生端 token 存储键 */
export const NATIVE_KEY_TOKEN = 'androidToken'

const LS_KEY_USER_INFO = 'userInfo'
const LS_KEY_TOKEN = 'token'

/**
 * 将 userInfo、token 同步到安卓原生
 * @param {string} userInfoRaw JSON 字符串或空
 * @param {string} token 访问令牌或空
 */
export function syncAuthToNative(userInfoRaw, token) {
    putString(NATIVE_KEY_USER_INFO, userInfoRaw || '')
    putString(NATIVE_KEY_TOKEN, token || '')
}

/**
 * 清空原生端登录缓存
 */
export function clearNativeAuth() {
    syncAuthToNative('', '')
}

/**
 * 本地无数据时从原生读取并回填 localStorage
 * @returns {{ userInfoRaw: string, token: string }}
 */
export function restoreAuthFromNative() {
    let userInfoRaw = localStorage.getItem(LS_KEY_USER_INFO) || ''
    let token = localStorage.getItem(LS_KEY_TOKEN) || ''

    if (!userInfoRaw) {
        const fromNative = getString(NATIVE_KEY_USER_INFO)
        if (fromNative) {
            userInfoRaw = fromNative
            localStorage.setItem(LS_KEY_USER_INFO, fromNative)
        }
    }
    if (!token) {
        const fromNative = getString(NATIVE_KEY_TOKEN)
        if (fromNative) {
            token = fromNative
            localStorage.setItem(LS_KEY_TOKEN, fromNative)
        }
    }
    return { userInfoRaw, token }
}

/**
 * 应用启动时调用：先恢复原生数据，再将当前登录态写回原生（防止仅本地有、原生无的情况）
 */
export function initAuthStorage() {
    restoreAuthFromNative()
    const userInfoRaw = localStorage.getItem(LS_KEY_USER_INFO) || ''
    const token = localStorage.getItem(LS_KEY_TOKEN) || ''
    if (userInfoRaw || token) {
        syncAuthToNative(userInfoRaw, token)
    }
}

/**
 * 登录成功：写入 localStorage、原生，并透传 setAppUserId
 * @param {string} token
 * @param {object|string} userInfo 用户对象或已序列化的 JSON 字符串
 */
export function saveAuthSession(token, userInfo) {
    const tk = String(token ?? '')
    const raw =
        typeof userInfo === 'string' ? userInfo : JSON.stringify(userInfo ?? {})
    localStorage.setItem(LS_KEY_TOKEN, tk)
    localStorage.setItem(LS_KEY_USER_INFO, raw)
    syncAuthToNative(raw, tk)
    try {
        const o = typeof userInfo === 'object' && userInfo !== null
            ? userInfo
            : JSON.parse(raw)
        const uid = o.userId ?? o.userid ?? o.id ?? ''
        setAppUserId(uid === undefined || uid === null ? '' : String(uid), tk)
    } catch {
        setAppUserId('', tk)
    }
}

/**
 * 仅更新 userInfo（如拉取用户信息接口），保持 token 并同步原生
 * @param {object} userInfoObj
 */
export function updateAuthUserInfo(userInfoObj) {
    const raw = JSON.stringify(userInfoObj ?? {})
    localStorage.setItem(LS_KEY_USER_INFO, raw)
    const token = localStorage.getItem(LS_KEY_TOKEN) || ''
    syncAuthToNative(raw, token)
}

/**
 * 退出登录：清空本地与原生登录态，并通知原生 setAppUserId 置空
 */
export function clearAuthSession() {
    localStorage.removeItem(LS_KEY_USER_INFO)
    localStorage.removeItem(LS_KEY_TOKEN)
    clearNativeAuth()
    setAppUserId('', '')
}

/**
 * 读取 token：本地优先，无则从原生恢复
 * @returns {string}
 */
export function getAuthToken() {
    let token = localStorage.getItem(LS_KEY_TOKEN) || ''
    if (!token) {
        token = getString(NATIVE_KEY_TOKEN)
        if (token) {
            localStorage.setItem(LS_KEY_TOKEN, token)
        }
    }
    return token
}

/**
 * 读取 userInfo 原始 JSON 字符串：本地优先，无则从原生恢复
 * @returns {string}
 */
export function getAuthUserInfoRaw() {
    let raw = localStorage.getItem(LS_KEY_USER_INFO) || ''
    if (!raw) {
        raw = getString(NATIVE_KEY_USER_INFO)
        if (raw) {
            localStorage.setItem(LS_KEY_USER_INFO, raw)
        }
    }
    return raw
}

/**
 * 解析后的 userInfo 对象，失败返回 null
 * @returns {object|null}
 */
export function getAuthUserInfo() {
    try {
        const raw = getAuthUserInfoRaw()
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

/**
 * 是否已登录（本地与原生均无 token 或 userInfo 时视为未登录）
 * @returns {boolean}
 */
export function isAuthLoggedIn() {
    return !!(getAuthToken() && getAuthUserInfoRaw())
}
